import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PremiumCard } from "../components/PremiumCard";
import { PremiumButton } from "../components/PremiumButton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { fadeIn, slideUp, staggerContainer } from "../animations";
import * as Icons from "lucide-react";
import { useState } from "react";

export const C2BFAQ = () => {
  const [refFAQ, isFAQVisible] = useScrollReveal({ once: true });
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Wie schnell erhalte ich mein Geld?",
      answer: "Nach Prüfung Ihres Geräts erhalten Sie die Auszahlung innerhalb von 1-2 Werktagen per Banküberweisung."
    },
    {
      question: "Was passiert mit meinen Daten?",
      answer: "Wir löschen alle Daten professionell und DSGVO-konform. Ihre Privatsphäre ist zu 100% geschützt."
    },
    {
      question: "Muss ich für den Versand zahlen?",
      answer: "Nein, der Versand ist für Sie komplett kostenlos. Wir stellen Ihnen ein versichertes Versandlabel zur Verfügung."
    },
    {
      question: "Was ist, wenn das Gerät nicht dem angegebenen Zustand entspricht?",
      answer: "Wir melden uns bei Ihnen und machen ein neues, angepasstes Angebot. Sie können dann entscheiden, ob Sie es annehmen oder das Gerät kostenfrei zurückerhalten möchten."
    },
    {
      question: "Welche Geräte kaufen Sie an?",
      answer: "Wir kaufen Laptops und Smartphones aller gängigen Marken an - von Apple, Samsung, HP, Dell, Lenovo und vielen mehr."
    },
    {
      question: "Muss das Gerät funktionieren?",
      answer: "Idealerweise ja, aber auch defekte Geräte kaufen wir an. Der Ankaufspreis richtet sich nach dem Zustand."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1f7db6] via-[#143e6e] to-[#1f7db6]" />
        
        <motion.div
          className="absolute top-20 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h1 variants={slideUp} className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
            Häufige Fragen
          </motion.h1>
          <motion.p variants={slideUp} className="text-xl sm:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Alles, was Sie über den Verkauf Ihres Geräts wissen müssen
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt verkaufen
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section ref={refFAQ} className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate={isFAQVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="max-w-3xl mx-auto space-y-4"
          >
            {faqs.map((faq, idx) => (
              <motion.div key={idx} variants={slideUp}>
                <PremiumCard className="overflow-hidden">
                  <button
                    onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                    className="w-full p-6 text-left flex items-center justify-between gap-4 hover:bg-gray-50/50 transition-colors"
                  >
                    <h3 className="font-bold text-lg text-[#143e6e]">{faq.question}</h3>
                    <motion.div
                      animate={{ rotate: openIndex === idx ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Icons.ChevronDown className="text-[#1f7db6] flex-shrink-0" size={24} />
                    </motion.div>
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: openIndex === idx ? "auto" : 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-700">
                      {faq.answer}
                    </div>
                  </motion.div>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1f7db6] via-[#143e6e] to-[#1f7db6]" />
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h2 variants={slideUp} className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Noch Fragen?
          </motion.h2>
          <motion.p variants={slideUp} className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Kontaktieren Sie uns oder starten Sie direkt mit Ihrem Angebot
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt Angebot erhalten
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};
